import React, { useState, useEffect } from "react";
import "../styles/Global.css";
import styles from "./Login.module.css";
import { useAuthContext } from "../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import WelcomePanel from "../components/auth/WelcomePanel";
import InputField from "../components/auth/InputField";
import Footer from "../components/Footer";

/**
 * ResetPassword
 * -------------
 * This page is loaded after the user clicks the **password-reset email** sent by Supabase.
 *
 * For regular users:
 * - Supabase automatically appends access & refresh tokens to the URL hash
 * - We show a "New password" form and call `supabase.auth.updateUser` to set the new password
 * - On success we redirect back to `/login`
 *
 * For legacy users:
 * - User metadata contains `legacy_user: true` and `legacy_token`
 * - After successful password reset, we trigger migration to transfer credits and questions
 * - On success we redirect to dashboard with welcome message
 */
const ResetPassword: React.FC = () => {
  const { supabase, user } = useAuthContext();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [isLegacyUser, setIsLegacyUser] = useState(false);
  const [legacyEmail, setLegacyEmail] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check for legacy user when user auth state changes
  useEffect(() => {
    const checkUserMetadata = async () => {
      if (user?.user_metadata) {
        const metadata = user.user_metadata;

        // Check if user has legacy token (could be from before migration)
        if (metadata.legacy_user === true && metadata.legacy_token) {
          // Verify with backend if user is actually still unmigrated
          try {
            const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";
            const response = await fetch(`${API_URL}/api/check-legacy-user`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                email: metadata.legacy_email || user.email,
              }),
            });

            if (response.ok) {
              const legacyStatus = await response.json();

              // Only treat as legacy user if they exist in legacy_users AND are not migrated
              if (legacyStatus.is_legacy_user && !legacyStatus.migrated) {
                setIsLegacyUser(true);
                setLegacyEmail(metadata.legacy_email || user.email);
              } else {
                setIsLegacyUser(false);
              }
            } else {
              setIsLegacyUser(false);
            }
          } catch (err) {
            setIsLegacyUser(false);
          }
        }
      }
    };

    checkUserMetadata();
  }, [user]);

  // Legacy fallback: also check URL parameters for backward compatibility
  const urlLegacyCheck = searchParams.get("legacy") === "true";
  const urlLegacyEmail = searchParams.get("email");

  // Use metadata detection first, fall back to URL parameters
  const isLegacyReset = isLegacyUser || urlLegacyCheck;
  const finalLegacyEmail = legacyEmail || urlLegacyEmail;

  // Basic client-side validation so we don't spam the API unnecessarily
  const validate = () => {
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters long");
      return false;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  /**
   * Handle legacy user migration after password reset
   */
  const handleLegacyMigration = async (
    userEmail: string,
    supabaseUserId: string
  ) => {
    setMigrating(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";
      
      const requestBody = {
        email: userEmail,
        supabase_user_id: supabaseUserId,
      };

      const response = await fetch(`${API_URL}/api/migrate-legacy-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Migration failed. Please contact support."
        );
      }

      const migrationResult = await response.json();

      // Show success notification
      const creditsText = migrationResult.legacy_data?.credits_transferred || 0;
      const questionsText =
        migrationResult.questions_migration?.successful || 0;

      (window as any).showNotification?.(
        `🎉 Welcome back! Your account has been successfully migrated with ${creditsText} credits and ${questionsText} questions.`,
        "success"
      );

      // Redirect to dashboard
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Migration failed. Please contact support."
      );
    } finally {
      setMigrating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.auth.updateUser({ password });
      if (error) {
        setError(
          error.message || "Failed to update password, link may have expired."
        );
        return;
      }

      setSuccess(true);

      // Handle legacy user migration
      if (isLegacyReset && finalLegacyEmail) {
        // Use user ID from the updateUser response or from context
        const userId = data?.user?.id || user?.id;

        if (userId) {
          await handleLegacyMigration(finalLegacyEmail, userId);
        } else {
          // Wait a moment for auth context to update and try again
          setTimeout(async () => {
            // Check auth context again after a brief delay
            const { data: currentUser } = await supabase.auth.getUser();
            const currentUserId = currentUser?.user?.id;

            if (currentUserId) {
              await handleLegacyMigration(finalLegacyEmail, currentUserId);
            } else {
              setError("Unable to complete migration. Please contact support.");
            }
          }, 1000);
        }
      } else if (!isLegacyReset) {
        // Regular user - show success and redirect to login
        (window as any).showNotification?.(
          "✅ Password updated! You can now log in.",
          "success"
        );
        // Give the user a brief moment to read the toast, then redirect.
        setTimeout(() => navigate("/login"), 1500);
      } else {
        setError(
          "Migration parameters missing. Please try the reset process again."
        );
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unexpected error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.authLayoutOuter}>
        <div className={styles.authLayoutContent}>
          {/* Left panel */}
          <div className={styles.leftPanel}>
            <WelcomePanel
              title={isLegacyReset ? "Welcome Back!" : "Create New Password"}
              subtitle={
                isLegacyReset
                  ? "Complete your account migration by setting a new password"
                  : "Choose a strong password to secure your account"
              }
            />
          </div>

          {/* Right panel - form */}
          <div className={styles.rightPanel}>
            <div className={styles.resetPasswordForm}>
              <h2 className={styles.resetPasswordTitle}>
                {isLegacyReset ? "Account Migration" : "Reset Password"}
              </h2>

              <p className={styles.resetPasswordSubtitle}>
                {isLegacyReset
                  ? "Enter your new password to complete the migration process."
                  : "Enter and confirm your new password below."}
              </p>

              {error && <div className={styles.errorMessage}>{error}</div>}

              {success && migrating ? (
                <div className={styles.migrationLoadingContainer}>
                  <div className={styles.migrationSpinner} />
                  <h3 className={styles.migrationLoadingTitle}>
                    Migrating Your Account...
                  </h3>
                  <p className={styles.migrationLoadingMessage}>
                    We're transferring your credits and questions. This may take a
                    few moments.
                  </p>
                </div>
              ) : success ? (
                <div className={styles.resetPasswordSuccessContainer}>
                  <div className={styles.resetPasswordSuccessIcon}>
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 12l2 2 4-4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3 className={styles.resetPasswordSuccessTitle}>
                    {isLegacyReset
                      ? "Migration Successful!"
                      : "Password Updated!"}
                  </h3>
                  <p className={styles.resetPasswordSuccessMessage}>
                    {isLegacyReset
                      ? "Redirecting to your dashboard..."
                      : "Redirecting to login..."}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <InputField
                    label="New Password"
                    type="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setPassword(e.target.value)
                    }
                    required
                  />

                  <InputField
                    label="Confirm Password"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setConfirm(e.target.value)
                    }
                    required
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    className={styles.submitButton}
                  >
                    {loading
                      ? "UPDATING..."
                      : isLegacyReset
                      ? "COMPLETE MIGRATION"
                      : "UPDATE PASSWORD"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ResetPassword;
